import React, { useState, useEffect, useCallback } from 'react';
import { PageHeader, Card, Button, Modal } from '../components/UI';
import api, { documentApi } from '../api';
import * as I from 'lucide-react';
import { toast } from 'react-toastify';

const formatCurrency = (amount) => {
    if (amount == null) return 'N/A';
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        minimumFractionDigits: 2
    }).format(amount);
};

const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'Invalid Date';
        const day = date.getDate();
        let daySuffix = 'th';
        if (day === 1 || day === 21 || day === 31) daySuffix = 'st';
        else if (day === 2 || day === 22) daySuffix = 'nd';
        else if (day === 3 || day === 23) daySuffix = 'rd';
        return `${day}${daySuffix} ${date.toLocaleDateString('en-IN', { month: 'short' })} ${date.getFullYear()}`;
    } catch (e) {
        console.warn('Invalid date format:', dateString);
        return 'Invalid Date';
    }
};

const ApprovalDetailPage = ({
    approval,
    onBack = () => window.history.back(),
    onApprove = (id) => {
        toast.warning('Approve handler not implemented')
    },
    onReject = (id) => {
        toast.warning('Reject handler not implemented')
    }
}) => {
    const [dueDate, setDueDate] = useState(approval?.dueDate || '');
    const [savingDueDate, setSavingDueDate] = useState(false);
    const [showPdf, setShowPdf] = useState(false);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [invoice, setInvoice] = useState(approval);

    // Update local state when approval prop changes
    useEffect(() => {
        if (approval) {
            setInvoice(approval);
        }
    }, [approval]);

    // TDS Rules state
    const [tdsRules, setTdsRules] = useState([]);
    const [loadingTdsRules, setLoadingTdsRules] = useState(false);

    // Edit amounts state
    const [isEditingAmounts, setIsEditingAmounts] = useState(false);
    const [editingAmounts, setEditingAmounts] = useState({
        subtotal: approval?.subtotal || 0,
        tax: approval?.tax || 0,
        tdsAmount: approval?.tdsAmount || 0,
        invoicePayable: approval?.invoicePayable || 0,
        totalAmount: approval?.totalAmount || 0
    });
    const [savingAmounts, setSavingAmounts] = useState(false);

    // TDS Edit modal state
    const [isTdsEditModalOpen, setIsTdsEditModalOpen] = useState(false);
    const [updatingTds, setUpdatingTds] = useState(false);
    const [tdsFormData, setTdsFormData] = useState({
        tdsRuleId: '',
        tdsPercentage: 0
    });

    // Fetch TDS Rules
    const fetchTdsRules = useCallback(async () => {
        setLoadingTdsRules(true);
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                return;
            }

            const response = await api.get('/tds-rules', {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            const rules = Array.isArray(response.data) ? response.data : [];
            setTdsRules(rules);
        } catch (error) {
            console.error('Error fetching TDS rules:', error);
            toast.error(error.response?.data?.message || 'Failed to fetch TDS rules');
        } finally {
            setLoadingTdsRules(false);
        }
    }, []);

    // Fetch TDS rules on component mount
    useEffect(() => {
        fetchTdsRules();
    }, [fetchTdsRules]);

    // Get TDS rule description from ID
    const getTdsRuleDescription = useCallback((tdsRuleValue) => {
        if (!tdsRuleValue) return 'N/A';

        // Try to find by ID first (if rules are loaded)
        // This covers the case where 'tdsRuleValue' is an ID (e.g. "1")
        if (tdsRules.length > 0) {
            // Check if it matches an ID directly
            const ruleById = tdsRules.find(r => r.id.toString() === tdsRuleValue.toString());
            if (ruleById) {
                return ruleById.description;
            }
        }

        // If not found by ID (or rules not loaded), return as is
        // This covers the case where it's already a description
        return tdsRuleValue;
    }, [tdsRules]);

    // Handle amount edit
    const handleAmountEdit = () => {
        setIsEditingAmounts(true);
        setEditingAmounts({
            subtotal: approval?.subtotal || 0,
            tax: approval?.tax || 0,
            tdsAmount: approval?.tdsAmount || 0,
            invoicePayable: approval?.invoicePayable || 0,
            totalAmount: approval?.totalAmount || 0
        });
    };

    // Handle amount change
    const handleAmountChange = (field, value) => {
        const numValue = parseFloat(value) || 0;
        setEditingAmounts(prev => {
            const updated = { ...prev, [field]: numValue };

            // Recalculate TDS amount when subtotal changes
            if (field === 'subtotal') {
                // Use current TDS percentage to calculate new TDS amount
                const tdsPercentage = approval?.tdsPercentage || 0;
                if (tdsPercentage > 0) {
                    updated.tdsAmount = updated.subtotal * (tdsPercentage / 100);
                }
            }

            // Recalculate totals
            if (field === 'subtotal' || field === 'tax' || field === 'tdsAmount') {
                updated.totalAmount = updated.subtotal + updated.tax;
                updated.invoicePayable = updated.totalAmount - updated.tdsAmount;
            }

            return updated;
        });
    };

    // Handle amount update
    const handleAmountUpdate = async () => {
        setSavingAmounts(true);
        try {
            const token = localStorage.getItem('token');
            const response = await api.put(`/invoices/${approval.id}/amounts`, {
                subtotal: editingAmounts.subtotal,
                tax: editingAmounts.tax
            }, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            // Update local approval data
            Object.assign(approval, response.data);

            // Update editing amounts if in edit mode
            setEditingAmounts(prev => ({
                ...prev,
                subtotal: response.data.subtotal,
                tax: response.data.tax,
                tdsAmount: response.data.tdsAmount,
                invoicePayable: response.data.invoicePayable,
                totalAmount: response.data.totalAmount
            }));

            setIsEditingAmounts(false);
            toast.success('Invoice amounts updated successfully');
        } catch (error) {
            console.error('Error updating amounts:', error);
            toast.error(error.response?.data?.message || 'Failed to update amounts');
        } finally {
            setSavingAmounts(false);
        }
    };

    // Handle amount cancel
    const handleAmountCancel = () => {
        setIsEditingAmounts(false);
        setEditingAmounts({
            subtotal: approval?.subtotal || 0,
            tax: approval?.tax || 0,
            tdsAmount: approval?.tdsAmount || 0,
            invoicePayable: approval?.invoicePayable || 0,
            totalAmount: approval?.totalAmount || 0
        });
    };

    // Handle TDS edit click
    const handleTdsEditClick = useCallback(() => {
        // Set initial form data from the approval object
        setTdsFormData({
            tdsRuleId: approval?.tdsRule || approval?.tdsRuleId || '',
            tdsPercentage: approval?.tdsPercentage || 0
        });
        // Fetch TDS rules if not already loaded
        if (tdsRules.length === 0) {
            fetchTdsRules();
        }
        setIsTdsEditModalOpen(true);
    }, [approval, tdsRules, fetchTdsRules]);

    // Handle TDS update
    const handleTdsUpdate = async (e) => {
        e.preventDefault();
        if (!approval || !tdsFormData.tdsRuleId) {
            toast.error('Please select a valid TDS tax section for this invoice');
            return;
        }

        setUpdatingTds(true);
        try {
            const token = localStorage.getItem('token');
            // Find the selected rule with loose comparison to handle string/number IDs
            const selectedRule = tdsRules.find(rule =>
                rule.id.toString() === tdsFormData.tdsRuleId.toString() ||
                rule.id === tdsFormData.tdsRuleId
            );

            if (!selectedRule) {
                toast.error('Selected TDS tax rule not found for this invoice');
                setUpdatingTds(false);
                return;
            }

            const tdsPercentage = parseFloat(selectedRule.percentage);
            const tdsRuleId = selectedRule.id;

            const response = await api.put(
                `/invoices/${approval.id}/tds`,
                {
                    tdsRuleId: tdsRuleId,
                    tdsRuleDescription: selectedRule.description,
                    tdsPercentage: tdsPercentage
                },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    withCredentials: true
                }
            );

            // Update the local approval data with the response from backend
            setInvoice(response.data);

            toast.success('TDS tax rate updated successfully for invoice');
            setIsTdsEditModalOpen(false);
        } catch (error) {
            console.error('Error updating TDS:', {
                error: error,
                response: error.response?.data
            });
            toast.error(error.response?.data?.message || 'Failed to update TDS tax rate for invoice');
        } finally {
            setUpdatingTds(false);
        }
    };

    const buildPdfUrl = (rawUrl) => {
        if (!rawUrl) return null;
        if (!rawUrl.startsWith('http')) {
            return `https://drive.google.com/file/d/${rawUrl}/preview`;
        }
        if (rawUrl.includes('drive.google.com/file/d/')) {
            const match = rawUrl.match(/\/file\/d\/([^/]+)/);
            if (match && match[1]) {
                return `https://drive.google.com/file/d/${match[1]}/preview`;
            }
        }
        return rawUrl;
    };

    // If approval is not provided, show a loading state
    if (!approval) {
        return (
            <div className="p-4 sm:p-6 lg:p-8 space-y-6 bg-gray-50 min-h-screen">
                <PageHeader
                    title="Loading..."
                    subtitle="Loading approval details"
                />
                <Card className="p-6 text-center">
                    <div className="flex justify-center">
                        <I.Loader2 className="animate-spin h-8 w-8 text-blue-500" />
                    </div>
                    <p className="mt-2 text-gray-600">Loading approval details, please wait...</p>
                </Card>
            </div>
        );
    }

    const isInvoiceLoading = !invoice;
    if (isInvoiceLoading) {
        return (
            <div className="p-4 sm:p-6 lg:p-8 space-y-6 bg-gray-50 min-h-screen">
                <PageHeader
                    title="Approval Details"
                    subtitle="No invoice selected for approval."
                    actions={[
                        <Button key="back" variant="outline" onClick={onBack}>
                            <I.ArrowLeft className="w-4 h-4 mr-1" /> Back to Approvals
                        </Button>
                    ]}
                />
                <Card className="p-6 text-gray-600 text-sm">
                    Please go back to Approvals and choose an invoice to view.
                </Card>
            </div>
        );
    }

    const handleViewHardCopy = async () => {
        console.log('[APPROVAL-DETAIL] handleViewHardCopy called');
        console.log('[APPROVAL-DETAIL] Invoice data:', invoice);

        // Check for S3 key in invoice (Now using documentUrl field)
        const documentUrlS3Key = invoice?.documentUrl;
        console.log('[APPROVAL-DETAIL] Document URL S3 key:', documentUrlS3Key);

        if (!documentUrlS3Key) {
            console.log('[APPROVAL-DETAIL] No document URL found, showing warning');
            toast.warning('No invoice document is available for this record.');
            return;
        }

        console.log('[APPROVAL-DETAIL] Starting API call to get document URL');
        try {
            // Get pre-signed URL from backend
            console.log('[APPROVAL-DETAIL] Fetching document URL for invoice ID:', invoice.id);
            const response = await documentApi.getInvoiceDocumentUrl(invoice.id);
            console.log('[APPROVAL-DETAIL] API response:', response);
            const documentUrl = response.data; // Backend returns string directly
            console.log('[APPROVAL-DETAIL] Extracted document URL:', documentUrl);

            if (!documentUrl) {
                console.log('[APPROVAL-DETAIL] Empty document URL received, showing warning');
                toast.warning('No invoice document is available for this record.');
                return;
            }

            setPdfUrl(documentUrl);
            setShowPdf(true);
            console.log('[APPROVAL-DETAIL] PDF URL set and modal shown');
        } catch (error) {
            console.error('[APPROVAL-DETAIL] Error fetching document URL:', error);
            toast.error('Failed to fetch document URL. Please try again or contact support.');
        }
    };

    const handleDueDateUpdate = async () => {
        if (!invoice) {
            toast.error('No invoice data available');
            return;
        }
        if (!invoice?.id) {
            toast.error('Cannot update due date: invoice ID is missing.');
            return;
        }

        if (!dueDate) {
            toast.warning('Please select a valid due date.');
            return;
        }

        // Show confirmation toast
        toast(
            <div className="flex flex-col space-y-3 p-2">
                <div className="flex items-center">
                    <I.Calendar className="w-5 h-5 text-blue-500 mr-2" />
                    <p className="text-sm font-medium text-gray-800">Update Due Date</p>
                </div>
                <p className="text-sm text-gray-600 ml-7">
                    Are you sure you want to update the due date to {new Date(dueDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}?
                </p>
                <div className="flex justify-end space-x-3 mt-2">
                    <button
                        className="px-4 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                        onClick={(e) => {
                            e.stopPropagation();
                            toast.dismiss();
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        className="px-4 py-1.5 text-xs font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                        onClick={async (e) => {
                            e.stopPropagation();
                            try {
                                setSavingDueDate(true);
                                const payload = { dueDate };
                                await api.put(`/invoices/${invoice.id}/due-date`, payload);
                                toast.dismiss();
                                toast.success('Due date updated successfully.', {
                                    position: 'top-center',
                                    autoClose: 3000,
                                    hideProgressBar: false,
                                    closeOnClick: true,
                                    pauseOnHover: true,
                                    draggable: true,
                                    className: 'text-center',
                                    bodyClassName: 'flex justify-center',
                                });
                            } catch (error) {
                                console.error('Failed to update due date:', error);
                                toast.error('Failed to update due date. Please try again.');
                            } finally {
                                setSavingDueDate(false);
                            }
                        }}
                        disabled={savingDueDate}
                    >
                        {savingDueDate ? 'Updating...' : 'Update Date'}
                    </button>
                </div>
            </div>,
            {
                autoClose: false,
                closeButton: false,
                closeOnClick: false,
                draggable: false,
                position: 'top-center',
                className: 'min-w-[320px] bg-white border border-gray-200 shadow-lg rounded-lg',
                bodyClassName: 'p-0',
                hideProgressBar: true,
            }
        );
    };

    const vendorName = invoice.vendorName || `ID: ${invoice.vendorId ?? 'N/A'}`;
    const isPendingApproval = String(invoice.status || '').toUpperCase() === 'PENDING_APPROVAL';

    return (
        <div className="p-4 sm:p-6 lg:p-8 space-y-4 bg-gray-50 min-h-screen">
            <PageHeader
                title="Approval Details"
                subtitle="Review the full details of the selected approval"
                actions={[
                    <Button key="back" variant="outline" onClick={onBack}>
                        <I.ArrowLeft className="w-4 h-4 mr-1" /> Back to Approvals
                    </Button>,
                    <Button key="hardcopy" variant="outline" onClick={handleViewHardCopy}>
                        <I.FileText className="w-4 h-4 mr-1" /> View Document
                    </Button>
                ]}
            />

            <Card className="p-6 space-y-4">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b pb-4">
                    <div>
                        <p className="text-xs uppercase tracking-wide text-gray-500">Invoice Number</p>
                        <p className="text-xl font-semibold text-gray-900 break-words">
                            {invoice.invoiceNumber || invoice.id}
                        </p>
                    </div>
                    <div className="flex flex-col items-start md:items-end gap-1">
                        <div className="text-right">
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Status</p>
                            <p className="text-sm font-semibold text-gray-900 tracking-wide">
                                {invoice.status || 'UNKNOWN'}
                            </p>
                        </div>
                        {isPendingApproval && (
                            <div className="flex items-center justify-end gap-2 mt-1">
                                <Button
                                    size="xs"
                                    variant="outline"
                                    onClick={() => {
                                        toast(
                                            <div className="flex flex-col space-y-3 p-2">
                                                <div className="flex items-center">
                                                    <I.AlertCircle className="w-5 h-5 text-blue-500 mr-2" />
                                                    <p className="text-sm font-medium text-gray-800">Confirm Invoice Approval</p>
                                                </div>
                                                <p className="text-sm text-gray-600 ml-7">This will approve the invoice and move it to payment processing queue.</p>
                                                <div className="flex justify-end space-x-3 mt-2">
                                                    <button
                                                        className="px-4 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            toast.dismiss();
                                                        }}
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button
                                                        className="px-4 py-1.5 text-xs font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            toast.dismiss();
                                                            onApprove(invoice.id);
                                                        }}
                                                    >
                                                        Approve
                                                    </button>
                                                </div>
                                            </div>,
                                            {
                                                autoClose: false,
                                                closeButton: false,
                                                closeOnClick: false,
                                                draggable: false,
                                                position: 'top-center',
                                                className: 'min-w-[320px] bg-white border border-gray-200 shadow-lg rounded-lg',
                                                bodyClassName: 'p-0',
                                                hideProgressBar: true,
                                            }
                                        );
                                    }}
                                    className="border border-green-400 text-green-600 bg-white px-3 py-1 h-7 text-xs rounded-md hover:bg-green-50"
                                >
                                    Approve
                                </Button>
                                <Button
                                    size="xs"
                                    variant="outline"
                                    onClick={() => {
                                        toast(
                                            <div className="flex flex-col space-y-3 p-2">
                                                <div className="flex items-center">
                                                    <I.AlertTriangle className="w-5 h-5 text-amber-500 mr-2" />
                                                    <p className="text-sm font-medium text-gray-800">Confirm Invoice Rejection</p>
                                                </div>
                                                <p className="text-sm text-gray-600 ml-7">This will reject the invoice and it will need to be resubmitted for approval.</p>
                                                <div className="flex justify-end space-x-3 mt-2">
                                                    <button
                                                        className="px-4 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            toast.dismiss();
                                                        }}
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button
                                                        className="px-4 py-1.5 text-xs font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            toast.dismiss();
                                                            onReject(invoice.id);
                                                        }}
                                                    >
                                                        Reject
                                                    </button>
                                                </div>
                                            </div>,
                                            {
                                                autoClose: false,
                                                closeButton: false,
                                                closeOnClick: false,
                                                draggable: false,
                                                position: 'top-center',
                                                className: 'min-w-[320px] bg-white border border-gray-200 shadow-lg rounded-lg',
                                                bodyClassName: 'p-0',
                                                hideProgressBar: true,
                                            }
                                        );
                                    }}
                                    className="border border-red-400 text-red-600 bg-white px-3 py-1 h-7 text-xs rounded-md hover:bg-red-50"
                                >
                                    Reject
                                </Button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Row 1: Invoice details, aligned with InvoiceDetailPage */}
                <div className="grid grid-cols-1 gap-6">
                    <div>
                        <div className="rounded-lg border bg-white p-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-3 text-sm">
                                <div>
                                    <p className="text-gray-500">Vendor</p>
                                    <p className="font-medium text-gray-900 break-words">{vendorName}</p>
                                </div>
                                <div>
                                    <p className="text-gray-500">Invoice Date</p>
                                    <p className="font-medium text-gray-900">{formatDate(invoice.invoiceDate)}</p>
                                </div>
                                <div>
                                    <p className="text-gray-500">Date Received</p>
                                    <p className="font-medium text-gray-900">{formatDate(invoice.dateReceived)}</p>
                                </div>
                                <div>
                                    <p className="text-gray-500">Due Date</p>
                                    <div className="flex items-center gap-1 mt-1">
                                        <input
                                            type="date"
                                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm text-gray-900 bg-white focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                                            value={dueDate || ''}
                                            onChange={(e) => setDueDate(e.target.value)}
                                        />
                                        <Button
                                            size="xs"
                                            variant="outline"
                                            className="border border-gray-300 px-1.5 py-0 h-7 text-xs hover:bg-gray-100"
                                            onClick={handleDueDateUpdate}
                                            disabled={savingDueDate}
                                        >
                                            {savingDueDate ? 'Saving...' : 'Update'}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Row 2: TDS details and Invoice summary on the same line */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <h3 className="text-sm font-semibold text-gray-900">TDS Details</h3>
                            {['pending_approval', 'pending'].includes(String(invoice.status || '').toLowerCase()) && (
                                <Button
                                    size="xs"
                                    variant="outline"
                                    onClick={handleTdsEditClick}
                                    className="px-3 py-1 text-xs"
                                >
                                    <I.Edit className="w-3 h-3 mr-1" />
                                    Edit TDS
                                </Button>
                            )}
                        </div>
                        <div className="rounded-lg border bg-white p-4 text-sm space-y-2">
                            {invoice.tdsAmount != null || invoice.tdsPercentage != null || invoice.tdsRule ? (
                                <>
                                    <div className="flex items-center justify-between">
                                        <p className="text-gray-500">TDS Rule</p>
                                        <p className="font-medium text-gray-900">{getTdsRuleDescription(invoice.tdsRule) || 'N/A'}</p>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <p className="text-gray-500">TDS Percentage</p>
                                        <p className="font-medium text-gray-900">
                                            {invoice.tdsPercentage != null ? `${invoice.tdsPercentage}%` : 'N/A'}
                                        </p>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <p className="text-gray-500">TDS Payment Status</p>
                                        <p className="font-medium text-gray-900">
                                            {String(invoice.status || '').toUpperCase() === 'TDS_PAID'
                                                ? 'TDS Paid'
                                                : 'TDS not paid'}
                                        </p>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <p className="text-gray-500">TDS Status</p>
                                        <p className="font-medium text-gray-900">
                                            {invoice.tdsAlreadyDeducted ? 'Already deducted by vendor' : 'Not Deducted'}
                                        </p>
                                    </div>

                                    {/* Shown ONLY when the vendor already withheld TDS on the invoice. */}
                                    {invoice.tdsAlreadyDeducted && (
                                        <>
                                            <div className="border-t my-2" />
                                            <div className="rounded-md bg-amber-50 border border-amber-200 px-2 py-1 text-xs text-amber-800">
                                                TDS already deducted by the vendor — the system did not deduct it again.
                                                Please verify the two amounts below match.
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <p className="text-gray-500">Amount withheld by vendor</p>
                                                <p className="font-medium text-gray-900">{formatCurrency(invoice.amountWithheld)}</p>
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <p className="text-gray-500">System-computed TDS (reference)</p>
                                                <p className="font-medium text-gray-900">
                                                    {invoice.tdsPercentage != null && invoice.subtotal != null
                                                        ? formatCurrency(invoice.subtotal * (invoice.tdsPercentage / 100))
                                                        : 'N/A'}
                                                </p>
                                            </div>
                                        </>
                                    )}

                                    <div className="border-t my-2" />
                                    <div className="flex items-center justify-between">
                                        <span className="text-gray-900 font-semibold">
                                            {invoice.tdsAlreadyDeducted ? 'TDS Amount (withheld by vendor)' : 'TDS Amount'}
                                        </span>
                                        <span className="text-lg font-bold text-gray-900">{formatCurrency(invoice.tdsAmount)}</span>
                                    </div>
                                </>
                            ) : (
                                <p className="text-gray-500">No TDS data available for this invoice.</p>
                            )}
                        </div>
                    </div>

                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <h3 className="text-sm font-semibold text-gray-900">Invoice Summary</h3>
                            {['pending_approval', 'pending'].includes(String(invoice.status || '').toLowerCase()) && (
                                <Button
                                    size="xs"
                                    variant={isEditingAmounts ? "outline" : "primary"}
                                    onClick={isEditingAmounts ? handleAmountUpdate : handleAmountEdit}
                                    disabled={savingAmounts}
                                    className="px-3 py-1 text-xs"
                                >
                                    {savingAmounts ? (
                                        <>
                                            <I.Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                            Updating...
                                        </>
                                    ) : isEditingAmounts ? (
                                        <>
                                            <I.Check className="w-3 h-3 mr-1" />
                                            Update
                                        </>
                                    ) : (
                                        <>
                                            <I.Edit className="w-3 h-3 mr-1" />
                                            Edit Amounts
                                        </>
                                    )}
                                </Button>
                            )}
                            {isEditingAmounts && ['pending_approval', 'pending'].includes(String(invoice.status || '').toLowerCase()) && (
                                <Button
                                    size="xs"
                                    variant="outline"
                                    onClick={handleAmountCancel}
                                    disabled={savingAmounts}
                                    className="px-3 py-1 text-xs ml-2"
                                >
                                    <I.X className="w-3 h-3 mr-1" />
                                    Cancel
                                </Button>
                            )}
                        </div>
                        <div className="rounded-lg border bg-white p-4 text-sm space-y-2">
                            <div className="flex items-center justify-between">
                                <span className="text-gray-500">Subtotal</span>
                                {isEditingAmounts && ['pending_approval', 'pending'].includes(String(invoice.status || '').toLowerCase()) ? (
                                    <input
                                        type="number"
                                        step="0.01"
                                        className="w-32 px-2 py-1 border border-gray-300 rounded text-sm text-gray-900 bg-white focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                                        value={editingAmounts.subtotal}
                                        onChange={(e) => handleAmountChange('subtotal', e.target.value)}
                                    />
                                ) : (
                                    <span className="font-medium text-gray-900">{formatCurrency(invoice.subtotal)}</span>
                                )}
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-gray-500">Tax</span>
                                {isEditingAmounts && ['pending_approval', 'pending'].includes(String(invoice.status || '').toLowerCase()) ? (
                                    <input
                                        type="number"
                                        step="0.01"
                                        className="w-32 px-2 py-1 border border-gray-300 rounded text-sm text-gray-900 bg-white focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                                        value={editingAmounts.tax}
                                        onChange={(e) => handleAmountChange('tax', e.target.value)}
                                    />
                                ) : (
                                    <span className="font-medium text-gray-900">{formatCurrency(invoice.tax)}</span>
                                )}
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-gray-500">TDS Amount</span>
                                <span className="font-medium text-gray-900">
                                    {formatCurrency(isEditingAmounts ? editingAmounts.tdsAmount : invoice.tdsAmount)}
                                </span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-gray-500">Invoice Payable</span>
                                <span className="font-semibold text-gray-900">
                                    {formatCurrency(isEditingAmounts ? editingAmounts.invoicePayable : invoice.invoicePayable)}
                                </span>
                            </div>
                            <div className="border-t my-2" />
                            <div className="flex items-center justify-between">
                                <span className="text-gray-900 font-semibold">Grand Total</span>
                                <span className="text-lg font-bold text-gray-900">
                                    {formatCurrency(isEditingAmounts ? editingAmounts.totalAmount : invoice.totalAmount)}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Row 3: Payment details full width */}
                {String(invoice.status || '').toUpperCase() === 'INVOICE_PAID' && (
                    <div>
                        <h3 className="text-sm font-semibold text-gray-900 mb-2">Payment Details</h3>
                        <div className="rounded-lg border bg-white p-4 text-sm grid grid-cols-1 gap-2">
                            <div className="flex items-center justify-between">
                                <p className="text-gray-500">Payment Date</p>
                                <p className="font-medium text-gray-900">{formatDate(invoice.invoicePaidDate)}</p>
                            </div>
                            <div className="flex items-center justify-between">
                                <p className="text-gray-500">Method</p>
                                <p className="font-medium text-gray-900">{invoice.invoicePaymentMethod || 'N/A'}</p>
                            </div>
                            <div className="flex items-center justify-between">
                                <p className="text-gray-500">Reference</p>
                                <p className="font-medium text-gray-900">{invoice.invoicePaymentReference || 'N/A'}</p>
                            </div>
                        </div>
                    </div>
                )}
            </Card>
            {showPdf && pdfUrl && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
                    <div className="bg-white rounded-lg shadow-xl w-[80vw] h-[85vh] flex flex-col overflow-hidden">
                        <div className="flex items-center justify-between px-4 py-2 border-b">
                            <span className="text-sm font-medium text-gray-700">Invoice Document</span>
                            <button
                                type="button"
                                onClick={() => setShowPdf(false)}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <I.X className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="flex-1">
                            {pdfUrl.toLowerCase().match(/\.(jpg|jpeg|png|gif|bmp|webp)$/i) ? (
                                // Display image in iframe
                                <iframe
                                    src={pdfUrl}
                                    title="Invoice Document"
                                    className="w-full h-full border-0"
                                    frameBorder="0"
                                    onLoad={() => console.log('[APPROVAL-DETAIL] Image iframe loaded')}
                                    onError={(e) => console.error('[APPROVAL-DETAIL] Image iframe error:', e)}
                                />
                            ) : (
                                // Display PDF with viewer parameters
                                <iframe
                                    src={`${pdfUrl}#toolbar=0&navpanes=0&scrollbar=0`}
                                    title="Invoice Document"
                                    className="w-full h-full border-0"
                                    frameBorder="0"
                                    onLoad={() => console.log('[APPROVAL-DETAIL] PDF iframe loaded')}
                                    onError={(e) => console.error('[APPROVAL-DETAIL] PDF iframe error:', e)}
                                />
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* TDS Edit Modal */}
            <Modal
                isOpen={isTdsEditModalOpen}
                onClose={() => !updatingTds && setIsTdsEditModalOpen(false)}
                title="Update TDS Percentage"
            >
                <form onSubmit={handleTdsUpdate} className="space-y-4">
                    {approval && (
                        <>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Select TDS Tax Section
                                </label>
                                <select
                                    value={tdsFormData.tdsRuleId}
                                    onChange={(e) => setTdsFormData(prev => ({ ...prev, tdsRuleId: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    required
                                >
                                    <option value="">Select TDS Section...</option>
                                    {tdsRules.map(rule => (
                                        <option key={rule.id} value={rule.id}>
                                            {rule.description}
                                        </option>
                                    ))}
                                </select>
                            </div>


                            <div className="bg-gray-50 p-3 rounded-md">
                                <p className="text-sm text-gray-600">
                                    <strong>Selected TDS Rate:</strong>
                                    {tdsFormData.tdsRuleId ? (
                                        (() => {
                                            const selectedRule = tdsRules.find(rule =>
                                                rule.id.toString() === tdsFormData.tdsRuleId.toString()
                                            );
                                            return selectedRule ? ` ${selectedRule.percentage}%` : ' 0%';
                                        })()
                                    ) : ' 0%'}
                                </p>
                            </div>

                            <div className="flex justify-end space-x-3 pt-4">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => setIsTdsEditModalOpen(false)}
                                    disabled={updatingTds}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    disabled={updatingTds || !tdsFormData.tdsRuleId}
                                >
                                    {updatingTds ? (
                                        <>
                                            <I.Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Updating...
                                        </>
                                    ) : (
                                        <>
                                            <I.Check className="w-4 h-4 mr-2" />
                                            Update TDS
                                        </>
                                    )}
                                </Button>
                            </div>
                        </>
                    )}
                </form>
            </Modal>
        </div>
    );
};

export default ApprovalDetailPage;
